import React from "react";

/**
 * OncoCadence brand mark — the original "C + pulse" glyph.
 *
 * Shape: an open 3/4 cycle ring (signals forward motion / treatment cycles)
 * threaded with a heartbeat line. Marker dot at the arc head.
 *
 * Color logic (NEW — theme aware):
 *   By default the icon paints itself in `--logo-color`, a CSS variable that
 *   each theme defines. The variable falls back to OncoCadence emerald
 *   (#10B981) which has solid contrast on dark, light, AND warm-tone themes.
 *   Themes that want a different mark colour (e.g. patient "Warm Sunset"
 *   uses a sand colour) just override --logo-color in their CSS scope.
 *
 * Pass `color="white"` etc. to force a fixed colour (used on the splash card).
 */
export default function Logo({
  size = 32,
  withWordmark = false,
  subtitle = null,
  className = "",
  monochrome = false,
  color: forcedColor = null,
}) {
  // CSS variable with sensible default → adapts to every theme automatically.
  const iconColor = forcedColor || "var(--logo-color, #10B981)";

  return (
    <span
      className={`inline-flex items-center gap-2.5 ${className}`}
      data-testid="app-logo"
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ color: iconColor, flexShrink: 0 }}
        aria-hidden="true"
      >
        {/* Cycle ring (3/4 arc, open at top-right to signal forward motion) */}
        <path
          d="M 24 6 A 18 18 0 1 0 42 24"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          fill="none"
          opacity="0.9"
        />
        {/* Marker dot at the arc head */}
        <circle cx="42" cy="24" r="2.6" fill="currentColor" />
        {/* Heartbeat line threaded through the ring */}
        <path
          d="M 11 24 L 17 24 L 19.5 19 L 23.5 30 L 26.5 21 L 29 24 L 37 24"
          stroke="currentColor"
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>

      {withWordmark && (
        <span className="leading-tight">
          <span
            className="font-display font-bold tracking-tight block"
            style={{ fontSize: Math.round(size * 0.62) + "px" }}
          >
            Onco
            <span
              style={{
                color: monochrome
                  ? undefined
                  : "var(--logo-color, #10B981)",
              }}
            >
              Cadence
            </span>
          </span>
          {subtitle && (
            <span className="block text-[10px] uppercase tracking-[0.16em] opacity-70 mt-0.5">
              {subtitle}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
